import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';

// Global singleton to ensure only one instance across the entire application
declare global {
  var __supabaseClient: SupabaseClient<Database> | undefined;
  var __supabaseInitialized: boolean | undefined;
}

const getSupabaseClient = (): SupabaseClient<Database> => {
  // Check if we already have a global instance
  if (globalThis.__supabaseClient && globalThis.__supabaseInitialized) {
    // Não logar toda vez para reduzir ruído no console
    return globalThis.__supabaseClient;
  }

  // Verificar se já estamos inicializando para evitar inicializações concorrentes
  if (globalThis.__supabaseInitialized) {
    if (!import.meta.env.PROD) {
      console.log('⏳ Supabase client is being initialized by another process, waiting...');
    }
    // Esperar um pouco e retornar a instância existente
    return globalThis.__supabaseClient as SupabaseClient<Database>;
  }

  // Marcar que estamos inicializando
  globalThis.__supabaseInitialized = true;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // Function to handle retrying fetch requests
  const fetchWithRetry = async (url: string | URL | Request, options?: RequestInit, retries = 3, backoff = 300) => {
    try {
      return await fetch(url, options);
    } catch (error) {
      if (retries <= 1) {
        console.error('Supabase fetch error (final attempt):', error);
        throw error;
      }
      
      console.warn(`Supabase fetch error, retrying (${retries-1} attempts left):`, error);
      
      // Wait for backoff duration before retrying
      await new Promise(resolve => setTimeout(resolve, backoff));
      
      // Retry with exponential backoff
      return fetchWithRetry(url, options, retries - 1, backoff * 2);
    }
  };

  // Create the Supabase client with enhanced configuration for session persistence
  globalThis.__supabaseClient = createClient<Database>(
    supabaseUrl, 
    supabaseAnonKey,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        flowType: 'pkce',
        detectSessionInUrl: true,
        storageKey: 'supabase-auth-token',
        storage: {
          getItem: (key: string) => {
            const item = localStorage.getItem(key);
            return item;
          },
          setItem: (key: string, value: string) => {
            localStorage.setItem(key, value);
          },
          removeItem: (key: string) => {
            localStorage.removeItem(key);
          }
        },
        debug: false, // Disable debug to reduce noise
      },
      global: {
        fetch: (url: string | URL | Request, options?: RequestInit) => {
          return fetchWithRetry(url, options)
            .catch(error => {
              console.error('❌ Supabase fetch error:', error);
              // Create a more user-friendly error
              const friendlyError = new Error(`Failed to connect to Supabase (${supabaseUrl}). Please check your network connection.`);
              // Use a type assertion for the error object to add the cause property
              (friendlyError as any).cause = error;
              throw friendlyError;
            });
        }
      }
    }
  );

  if (!import.meta.env.PROD) {
    console.log('✅ Single Supabase client initialized successfully');
  }
  
  // Garantir que a flag de inicialização esteja correta
  globalThis.__supabaseInitialized = true;
  
  return globalThis.__supabaseClient;
};

// Export the singleton instance
export const supabase = getSupabaseClient();

// For debugging purposes
export const getClientInfo = () => {
  return {
    hasInstance: !!globalThis.__supabaseClient,
    instanceId: globalThis.__supabaseClient ? Math.random().toString(36).substr(2, 9) : null
  };
}; 